import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import Colors from '../utilities/Colors';
import { onSetActiveGeography, useGlobalHoveredId, useGlobalSelectedId, useGlobalActiveGeography } from '../../data/StatusStore';
import { useGlobalSwarmCalibration } from '../../data/GlobalStore';
import { d2gRound, getTooltipGeoText } from '../utilities/Utilities';
import { siteConfig } from '../../config/siteConfig';

interface BaseDataItem {
  id: string;
  value: number;
}

interface SwarmChartDynamicProps {
  dataArray: BaseDataItem[];
  histMax: number;
  histMin: number;
  histNA: number;
  histLength: number;
  chartId: string;
  nodes: DataItem[];
  DESCRIPTOR: string;
  labelOnly?: any;
  data: any;
  context: string;
}

interface DataItem {
  id: string;
  value: number;
  x?: number;
  y?: number;
  tooltip?: any;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const RangeSwarmDynamic: React.FC<SwarmChartDynamicProps> = ({
  dataArray,
  chartId,
  histMax,
  histMin,
  histNA,
  histLength,
  nodes,
  DESCRIPTOR,
  labelOnly = null,
  data,
  context,
}) => {
  const useCase = context;
  const rg = context === "race and gender" ? "RG" : "";
  const width = "100%";
  const height = "100%";
  const svgRef = useRef<SVGSVGElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const swarmCalibration = useGlobalSwarmCalibration();
  const hoveredId = useGlobalHoveredId();
  const selectedId = useGlobalSelectedId();
  const activeGeography = useGlobalActiveGeography();
  const calibration = swarmCalibration?.RangeSwarm?.[`${activeGeography}${rg}`] ?? swarmCalibration?.[`${activeGeography}${rg}`];
  const radius = calibration?.radius ?? 1;
  const padding = calibration?.padding ?? 0.25;
  const segmentLengthPct = calibration?.segmentLengthPct ?? 6;

  useEffect(() => {
    const updateContainerWidth = () => {
      if (svgRef.current) {
        setContainerWidth(svgRef.current.getBoundingClientRect().width);
      }
    };

    window.addEventListener('swarm_resize', updateContainerWidth);
    updateContainerWidth();

    return () => window.removeEventListener('swarm_resize', updateContainerWidth);
  }, [hoveredId]);

  return (
    <>
      {nodes.map((node, i) => {
        const isSelected = node.id == selectedId;
        const isHovered = node.id == hoveredId;
        const fill = isSelected ? Colors.highlightSelect : isHovered ? Colors.highlightHover : 'none';
        if (!isSelected && !isHovered) return null;
        if ((node.x ?? -2) < 0) return null;

        const xCenter = node.x ?? 0;
        const halfSegment = segmentLengthPct / 2;
        const x1 = `${xCenter - halfSegment}%`;
        const x2 = `${xCenter + halfSegment}%`;
        const yPct = node.y
          ? `${((parseInt(height) / 2 - radius - padding - node.y) / parseInt(height)) * 100}%`
          : `${((parseInt(height) / 2 - radius - padding) / parseInt(height)) * 100}%`;

        return (
          <div key={`cont_dyn_${i}_${chartId}`}>
            <div
              key={`range_swarm_${isSelected ? 'selected' : 'hovered'}_${chartId}_${i}`}
              className='swarm-container swarm-container-dynamic'
              style={{ position: "absolute", top: useCase === "race and gender" ? "-25px" : "-40px", width: "100%" }}
            >
              <div
                className={`hist-label-rect ${isSelected ? 'popup-selected' : 'popup-hovered'}`}
                key={`range_swarm_${isSelected ? 'selected' : 'hovered'}_${chartId}_${i}_box`}
                style={{
                  width: useCase === "race and gender" ? "66px" : "80px",
                  height: useCase === "race and gender" ? "32px" : "50px",
                  backgroundColor: "#FAF5F5",
                  border: `1px solid ${fill}`,
                  position: "absolute",
                  fontSize: "12px",
                  textAlign: "center",
                  left: useCase === "race and gender"
                    ? node.x ? `calc(${node.x}% - (23px + ${node.x / 100 * (activeGeography === "Tract" ? 23 : 18)}px))` : "0"
                    : node.x ? `calc(${node.x}% - (30px + ${node.x / 100 * (activeGeography === "Tract" ? 23 : 18)}px))` : "0",
                  lineHeight: 1.25,
                  paddingTop: activeGeography === "Tract" || (siteConfig.ctx === "sbc" && activeGeography === "CD")
                    ? "0px" 
                    : useCase === "race and gender" 
                      ? "0px" 
                      : "8.5px",
                  color: `${fill}`,
                  borderRadius: '4px',
                }}
              >
                <div
                  key={`b_range_swarm_${isSelected ? 'selected' : 'hovered'}_${chartId}_${i}`}
                  style={{ zIndex: 10, position: "relative", lineHeight: (activeGeography === "Boro" && node.id === "5") ? /*0.825*/ 'unset' : 'unset', fontSize: node.tooltip ? "10px" : activeGeography !== "NTA" ? "12px" : "10px" }}
                >
                  {getTooltipGeoText(data.dataJSON, activeGeography, node.id)}<br /><b>{`${d2gRound(node.value, { DESCRIPTOR: DESCRIPTOR, rateTotal: true })}`}<br />{node.tooltip ? `(${d2gRound(node.tooltip, { DESCRIPTOR: labelOnly[0].sort.DESCRIPTOR, rateTotal: true })})` : ""}</b>
                </div>
                <div
                  key={`a_range_swarm_${isSelected ? 'selected' : 'hovered'}_${chartId}_${i}`}
                  style={{ width: "16px", height: "16px", backgroundColor: "#FAF5F5", border: `1px solid ${fill}`, position: "absolute", bottom: "-8.5px", left: "calc(50% - 8px)", rotate: "45deg", borderTop: "none", borderLeft: "none", zIndex: 9 }}
                ></div>
              </div>
            </div>
            <div key={`svg_range_swarm_${isSelected ? 'selected' : 'hovered'}_${chartId}_${i}`} className='swarm-container swarm-container-dynamic' style={{ position: "absolute", top: "0px", width: "100%" }}>
              <svg key={`svg_svg_range_swarm_${isSelected ? 'selected' : 'hovered'}_${chartId}_${i}`} ref={svgRef} width={width} height={height} className="swarm-chart-dynamic" style={{ overflow: 'visible' }}>
                <line
                  key={node.id}
                  x1={x1}
                  x2={x2}
                  y1={yPct}
                  y2={yPct}
                  stroke={fill}
                  strokeWidth={Math.max(2, radius * 2)}
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default RangeSwarmDynamic;